// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FormTextField } from '@/components/form-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import { getContacts } from '@/contact/api/get-contacts';
import { Contact, UpdateContactParameters } from '@/contact/api/types';
import { updateContact } from '@/contact/api/update-contact';
import {
  ContactFormData,
  ContactFormOutputData,
  contactFormSchema,
  normalizeNullable,
} from '@/contact/utilities/contact-form-schema';
import { FormCustomerSearch } from '@/customer/components/form-customer-search';
import { getNewCompanyURL } from '@/customer/utilities/get-new-customer-url';
import { toCappedSearchTerm } from '@/utilities/search-term';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';
import {
  SubmitHandler,
  useController,
  useForm,
  useWatch,
} from 'react-hook-form';

const DUPLICATE_CHECK_DEBOUNCE_MS = 30;

interface NameToCheck {
  customerId: string;
  firstName: string;
  lastName: string;
}

interface Props {
  isOpen: boolean;
  contact: Contact;
  onClose: () => void;
}

function getDefaultValues(contact: Contact): ContactFormData {
  return {
    customer: contact.customer,
    firstName: contact.firstName,
    lastName: contact.lastName,
    title: contact.title,
    email: contact.email,
    phone: contact.phone,
    comments: contact.comments,
  };
}

export function EditContactDialog(props: Props) {
  const queryClient = useQueryClient();
  const [emailToCheck, setEmailToCheck] = useState<string | null>(null);
  const [nameToCheck, setNameToCheck] = useState<NameToCheck | null>(null);

  const updateContactMutation = useMutation({
    mutationFn: (parameters: UpdateContactParameters) =>
      updateContact(props.contact.key, parameters),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact'] });
      close();
    },
  });

  const duplicateEmailQuery = useQuery({
    queryKey: ['contact', 'duplicate-email', props.contact.key, emailToCheck],
    queryFn: () =>
      getContacts({
        filter: { email: emailToCheck ?? undefined, isArchived: 'all' },
        page: 1,
        limit: 25,
      }),
    enabled: emailToCheck !== null,
  });

  const duplicateNameQuery = useQuery({
    queryKey: ['contact', 'duplicate-name', props.contact.key, nameToCheck],
    queryFn: () =>
      getContacts({
        filter: {
          customerIds: nameToCheck ? [nameToCheck.customerId] : undefined,
          search: nameToCheck
            ? toCappedSearchTerm(nameToCheck.firstName)
            : undefined,
          isArchived: 'all',
        },
        page: 1,
        limit: 25,
      }),
    enabled: nameToCheck !== null,
  });

  const { control, reset, handleSubmit, formState } = useForm<
    ContactFormData,
    unknown,
    ContactFormOutputData
  >({
    resolver: zodResolver(contactFormSchema),
    defaultValues: getDefaultValues(props.contact),
  });

  const emailController = useController({ control, name: 'email' });

  const watchedCustomer = useWatch({ control, name: 'customer' });
  const watchedFirstName = useWatch({ control, name: 'firstName' });
  const watchedLastName = useWatch({ control, name: 'lastName' });
  const watchedEmail = useWatch({ control, name: 'email' });

  const debouncedSetNameToCheck = useMemo(
    () =>
      debounce(
        (value: NameToCheck | null) => setNameToCheck(value),
        DUPLICATE_CHECK_DEBOUNCE_MS
      ),
    []
  );

  const debouncedSetEmailToCheck = useMemo(
    () =>
      debounce(
        (value: string | null) => setEmailToCheck(value),
        DUPLICATE_CHECK_DEBOUNCE_MS
      ),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetNameToCheck.cancel();
      debouncedSetEmailToCheck.cancel();
    };
  }, [debouncedSetNameToCheck, debouncedSetEmailToCheck]);

  useEffect(() => {
    debouncedSetEmailToCheck(normalizeNullable(watchedEmail));
  }, [watchedEmail, debouncedSetEmailToCheck]);

  useEffect(() => {
    const firstName = watchedFirstName.trim();
    const lastName = watchedLastName.trim();
    if (!watchedCustomer || firstName === '' || lastName === '') {
      debouncedSetNameToCheck(null);
      return;
    }
    debouncedSetNameToCheck({
      customerId: watchedCustomer.id,
      firstName,
      lastName,
    });
  }, [
    watchedCustomer,
    watchedFirstName,
    watchedLastName,
    debouncedSetNameToCheck,
  ]);

  useEffect(() => {
    if (props.isOpen) {
      reset(getDefaultValues(props.contact));
      debouncedSetEmailToCheck.cancel();
      debouncedSetNameToCheck.cancel();
      setEmailToCheck(null);
      setNameToCheck(null);
    }
  }, [
    props.contact,
    props.isOpen,
    reset,
    debouncedSetEmailToCheck,
    debouncedSetNameToCheck,
  ]);

  const close = () => {
    props.onClose();
    reset(getDefaultValues(props.contact));
    debouncedSetEmailToCheck.cancel();
    debouncedSetNameToCheck.cancel();
    setEmailToCheck(null);
    setNameToCheck(null);
  };

  const handleCreateCompany = (name: string) => {
    const url = getNewCompanyURL(name);
    window.open(url, '_blank');
  };

  const duplicateEmailMatches =
    duplicateEmailQuery.data?.contacts.filter(
      (contact) => contact.key !== props.contact.key
    ) ?? [];

  const hasDuplicateEmail =
    duplicateEmailMatches.length > 0 && emailToCheck !== null;

  const hasDuplicateName =
    nameToCheck !== null &&
    (duplicateNameQuery.data?.contacts ?? []).some(
      (contact) =>
        contact.key !== props.contact.key &&
        contact.firstName.trim().toLowerCase() ===
          nameToCheck.firstName.toLowerCase() &&
        contact.lastName.trim().toLowerCase() ===
          nameToCheck.lastName.toLowerCase()
    );

  const handleValidSubmission: SubmitHandler<ContactFormOutputData> = (
    data
  ) => {
    const parameters: UpdateContactParameters = {};

    if (formState.dirtyFields.customer) {
      parameters.customerId = data.customer.id;
    }

    if (formState.dirtyFields.firstName) {
      parameters.firstName = data.firstName.trim();
    }

    if (formState.dirtyFields.lastName) {
      parameters.lastName = data.lastName.trim();
    }

    if (formState.dirtyFields.title) {
      parameters.title = normalizeNullable(data.title);
    }

    if (formState.dirtyFields.email) {
      parameters.email = normalizeNullable(data.email);
    }

    if (formState.dirtyFields.phone) {
      parameters.phone = normalizeNullable(data.phone);
    }

    if (formState.dirtyFields.comments) {
      parameters.comments = normalizeNullable(data.comments);
    }

    if (Object.keys(parameters).length === 0) {
      close();
      return;
    }

    updateContactMutation.mutate(parameters);
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={props.isOpen}
      onClose={close}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(handleValidSubmission),
      }}
    >
      <SupportSnackbar
        isOpen={updateContactMutation.isError}
        message="An error occurred while updating the contact"
        onClose={updateContactMutation.reset}
      />
      <DialogTitle>Edit contact</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <FormCustomerSearch
            control={control}
            name="customer"
            label="Company"
            disableClearable
            onCreate={handleCreateCompany}
            filter={{ isArchived: false }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormTextField
              fullWidth
              control={control}
              name="firstName"
              label="First name"
            />
            <FormTextField
              fullWidth
              control={control}
              name="lastName"
              label="Last name"
            />
          </Stack>
          {hasDuplicateName && (
            <Alert severity="warning">
              A contact with this name already exists for this company. You can
              still continue.
            </Alert>
          )}
          <FormTextField
            fullWidth
            control={control}
            name="title"
            label="Title"
          />
          <TextField
            fullWidth
            size="small"
            name={emailController.field.name}
            value={emailController.field.value ?? ''}
            label="Email"
            inputRef={emailController.field.ref}
            onChange={emailController.field.onChange}
            onBlur={emailController.field.onBlur}
            error={Boolean(emailController.fieldState.error)}
            helperText={emailController.fieldState.error?.message}
          />
          {hasDuplicateEmail && (
            <Alert severity="warning">
              A contact with this email already exists. You can still continue.
            </Alert>
          )}
          <FormTextField
            fullWidth
            control={control}
            name="phone"
            label="Phone"
          />
          <FormTextField
            fullWidth
            isMultiline
            minimumRows={3}
            control={control}
            name="comments"
            label="Comments"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={updateContactMutation.isPending}>
          Cancel
        </Button>
        <LoadingButton
          type="submit"
          color="secondary"
          variant="contained"
          loading={updateContactMutation.isPending}
          disabled={!formState.isDirty}
        >
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
